import { useTheme } from 'next-themes';
import { flushSync } from 'react-dom';

// The `180ms` matches the default Tailwind transition duration so a CSS-only
// swap still feels consistent with the rest of the app.
const CSS_FALLBACK_DURATION_MS = 180;

/**
 * Animated theme toggle.
 *
 * - With the View Transitions API (Chrome / Edge / Safari TP) the swap gets
 *   a circular reveal anchored at the click event location (or a random point fallback).
 * - When the API is unavailable OR the user prefers reduced motion, we
 *   fall back to a brief CSS transition on the root element so the swap
 *   never feels like a hard flash.
 */
export function useAnimatedTheme() {
	const { theme, setTheme, resolvedTheme } = useTheme();

	const setAnimatedTheme = (newTheme: string) => {
		const root = document.documentElement;
		const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

		if (!document.startViewTransition || prefersReducedMotion) {
			// CSS-only fallback: a temporary transition on the root element so the
			// theme change animates smoothly. Transition events clear it afterward.
			if (!prefersReducedMotion) {
				const previousTransition = root.style.transition;
				root.style.transition = `background-color ${CSS_FALLBACK_DURATION_MS}ms ease, color ${CSS_FALLBACK_DURATION_MS}ms ease`;
				const cleanup = (): void => {
					root.style.transition = previousTransition;
					root.removeEventListener('transitionend', cleanup);
					root.removeEventListener('transitioncancel', cleanup);
				};
				root.addEventListener('transitionend', cleanup, { once: true });
				root.addEventListener('transitioncancel', cleanup, { once: true });
			}
			setTheme(newTheme);
			return;
		}

		root.style.setProperty('--x', `${Math.random() * 100}%`);
		root.style.setProperty('--y', `${Math.random() * 100}%`);

		document.startViewTransition(() => {
			flushSync(() => {
				setTheme(newTheme);
			});
		});
	};

	return {
		theme,
		resolvedTheme,
		setTheme: setAnimatedTheme,
	};
}
